import "@/App.css";
// Eager-imported CSS for standalone landing pages so react-snap can
// prerender them without hitting ChunkLoadError on lazy CSS chunks.
import "./pages/tylerLanding.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { QmoConfigProvider } from "@/context/QmoConfigContext";
import axios from "axios";
import { captureUtm, pingVisit, getStoredUtm } from "@/lib/utm";
import { captureCp, appendCpToUrl } from "@/lib/cpAttribution";
import { trackSurveyStart } from "@/lib/track";

// ─── Global axios UTM interceptor ───
//
// Auto-injects the visitor's stored UTM context into every POST to our
// own backend so conversions (waitlist, ZIP, applications, careers,
// podcast applications) are attributed without each form having to know
// about UTMs. Explicit fields on the form payload always win — utm is
// spread first, then the form data.
//
// FormData payloads (job application uploads) get the same treatment
// via `.append()`. Requests to 3rd party origins are skipped.
const _BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.interceptors.request.use((config) => {
  try {
    if (
      config.method &&
      config.method.toLowerCase() === "post" &&
      config.url &&
      _BACKEND_URL &&
      config.url.startsWith(_BACKEND_URL)
    ) {
      const utm = getStoredUtm();
      const utmEntries = Object.entries(utm || {}).filter(([, v]) => v);
      if (utmEntries.length > 0) {
        if (config.data instanceof FormData) {
          for (const [k, v] of utmEntries) {
            if (!config.data.has(k)) config.data.append(k, v);
          }
        } else if (
          config.data &&
          typeof config.data === "object" &&
          !Array.isArray(config.data)
        ) {
          config.data = { ...Object.fromEntries(utmEntries), ...config.data };
        } else if (config.data == null) {
          config.data = Object.fromEntries(utmEntries);
        }
      }
    }
  } catch {
    /* swallow — never break a request because of attribution */
  }
  return config;
});

// Eager: HomePage is the most common landing target; ship it in the initial bundle for fastest LCP.
import HomePage from "./pages/HomePage";
// Eager: WhyNotSliderPage is a high-traffic paid-social landing (/whynot, /bq01, /fb01, ...).
// It MUST be eager so the initial React hydration matches the prerendered HTML with zero
// intermediate Suspense fallback — otherwise mobile Safari flashes a white RouteLoader
// between the first painted slider and the hydrated slider.
import WhyNotSliderPage from "./pages/WhyNotSliderPage";
// Eager: slide data for the /fb02 LGBTQ+ variant (tiny module, no images bundled).
import FB02_SLIDES from "./pages/fb02Slides";
// Eager: MatchingModelPage is prerendered for SEO. It MUST be eager so the
// initial React hydration matches the prerendered HTML directly — a lazy()
// import would render the Suspense fallback (RouteLoader) during hydration,
// producing a React #418 mismatch against the fully-rendered prerender.
import MatchingModelPage from "./pages/MatchingModelPage";

// Lazy: all other routes are split into separate JS chunks and only fetched when navigated to.
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const WhyPage = lazy(() => import("./pages/WhyPage"));
const EvolutionPage = lazy(() => import("./pages/EvolutionPage"));
const WhatToExpectPage = lazy(() => import("./pages/WhatToExpectPage"));
const MatchExplainedPage = lazy(() => import("./pages/MatchExplainedPage"));
const ConnectExplainedPage = lazy(() => import("./pages/ConnectExplainedPage"));
const VerificationSubpage = lazy(() => import("./pages/VerificationSubpage"));
const VerificationPage = lazy(() => import("./pages/VerificationPage"));
// Cities directory retired 2026-02; the following lazy imports are no
// longer referenced from <Routes>. Preserved as commented-out imports
// so the modules themselves remain in the repo for rollback if needed.
// const ActiveCitiesPage = lazy(() => import("./pages/ActiveCitiesPage"));
// const ActiveCitiesPageV2 = lazy(() => import("./pages/ActiveCitiesPageV2"));
const MatchExamplePage = lazy(() => import("./pages/MatchExamplePage"));
const CityWaitlistPage = lazy(() => import("./pages/CityWaitlistPage"));
const GeneralWaitlistPage = lazy(() => import("./pages/GeneralWaitlistPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AnatomyPage = lazy(() => import("./pages/AnatomyPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ProblemPage = lazy(() => import("./pages/ProblemPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage"));
const ApplyPage = lazy(() => import("./pages/ApplyPage"));
const PressPage = lazy(() => import("./pages/PressPage"));
const AustinLandingPage = lazy(() => import("./pages/AustinLandingPage"));
const AustinSnapshotPage = lazy(() => import("./pages/AustinSnapshotPage"));
const PortlandSnapshotPage = lazy(() => import("./pages/PortlandSnapshotPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const HaevnLivePage = lazy(() => import("./pages/HaevnLivePage"));
const TylerLandingPage = lazy(() => import("./pages/TylerLandingPage"));
const MatchmakingLandingPage = lazy(() => import("./pages/MatchmakingLandingPage"));
const NoSwipingPage = lazy(() => import("./pages/NoSwipingPage"));
const RealMatchesPage = lazy(() => import("./pages/RealMatchesPage"));
const WhoItsForPage = lazy(() => import("./pages/WhoItsForPage"));
const MatchingPage = lazy(() => import("./pages/MatchingPage"));
const MatchMondayPage = lazy(() => import("./pages/MatchMondayPage"));
const MembershipPage = lazy(() => import("./pages/MembershipPage"));
const FoundersPage = lazy(() => import("./pages/FoundersPage"));
const TimePage = lazy(() => import("./pages/TimePage"));
const WhyHaevnPage = lazy(() => import("./pages/WhyHaevnPage"));
const NoPhotosPage = lazy(() => import("./pages/NoPhotosPage"));
const ProblemSubpage = lazy(() => import("./pages/ProblemSubpage"));
// const CitiesSubpage = lazy(() => import("./pages/CitiesSubpage")); // retired 2026-02
const DemoSubpage = lazy(() => import("./pages/DemoSubpage"));
const PerspectivePage = lazy(() => import("./pages/PerspectivePage"));
const PerspectiveDetailPage = lazy(() => import("./pages/PerspectiveDetailPage"));
const How80WorksPage = lazy(() => import("./pages/How80WorksPage"));
const BoqmGiveawayPage = lazy(() => import("./pages/BoqmGiveawayPage"));
const PartnerOnboardingPage = lazy(() => import("./pages/PartnerOnboardingPage"));
const PartnerPortalPage = lazy(() => import("./pages/PartnerPortalPage"));
const CrmApp = lazy(() => import("./pages/crm/CrmApp"));
const FieldIntakePage = lazy(() => import("./pages/FieldIntakePage"));
const RepFieldPage = lazy(() => import("./pages/RepFieldPage"));
const MeetupFunnelPage = lazy(() => import("./pages/MeetupFunnelPage"));
const MeetupManagePage = lazy(() => import("./pages/MeetupManagePage"));
const MeetupTermsPage = lazy(() => import("./pages/MeetupTermsPage"));
const CopPortalPage = lazy(() => import("./pages/cop/CopPortalPage"));
const PodcastHostPage = lazy(() => import("./pages/PodcastHostPage"));
const SwipeBaitPage = lazy(() => import("./pages/SwipeBaitPage"));
const StartPage = lazy(() => import("./pages/StartPage"));
const PortlandPage = lazy(() => import("./pages/PortlandPage"));
const EnmPage = lazy(() => import("./pages/EnmPage"));
const GayPage = lazy(() => import("./pages/GayPage"));
const PolyPage = lazy(() => import("./pages/PolyPage"));
const BlogIndexPage = lazy(() => import("./pages/BlogIndexPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const WhyNotPage = lazy(() => import("./pages/WhyNotPage"));
const SafetynetSliderPage = lazy(() => import("./pages/SafetynetSliderPage"));
const CommunityPartnerPage = lazy(() => import("./pages/CommunityPartnerPage"));
const AffiliateApplyPage = lazy(() => import("./pages/AffiliateApplyPage"));
const AffiliateTermsPage = lazy(() => import("./pages/AffiliateTermsPage"));
const AffiliateSetupPage = lazy(() => import("./pages/AffiliateSetupPage"));
const AffiliateLoginPage = lazy(() => import("./pages/AffiliateLoginPage"));
const AffiliateDashboardPage = lazy(() => import("./pages/AffiliateDashboardPage"));
const AffiliatePortalPage = lazy(() => import("./pages/affiliate/AffiliatePortalPage"));
const AffiliateJoinPage = lazy(() => import("./pages/AffiliateJoinPage"));
const AffiliateReferralRedirectPage = lazy(() => import("./pages/AffiliateReferralRedirectPage"));
const HaevnUniversityAffiliatePage = lazy(() => import("./pages/HaevnUniversityAffiliatePage"));
const CommunityPartnerTermsPage = lazy(() => import("./pages/CommunityPartnerTermsPage"));
const CommunityPartnerDashboardPage = lazy(() => import("./pages/CommunityPartnerDashboardPage"));
const PartnerWarmupPage = lazy(() => import("./pages/PartnerWarmupPage"));
const DesignLabPage = lazy(() => import("./pages/DesignLabPage"));
const CastingPage = lazy(() => import("./pages/CastingPage").then((m) => ({ default: m.CastingIndexPage })));
const CastingProjectPage = lazy(() => import("./pages/CastingPage").then((m) => ({ default: m.CastingProjectPage })));
const Landing10072026A = lazy(() => import("./pages/Landing10072026A"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * Sends an explicit GA4 page_view on every route change (initial + SPA navs).
 * We disable GA's automatic page_view in index.html (send_page_view: false)
 * because BrowserRouter doesn't trigger a real navigation, so GA would never
 * see route changes on its own. Firing here gives GA the correct page_path,
 * page_title, and page_location so engagement timers attach to the right
 * report row (the previous setup left /enm with 0s engagement time).
 */
function RouteTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const path = pathname + (search || "");
    // First: capture any UTM params off the URL and stash them in
    // sessionStorage so subsequent conversions can be attributed.
    captureUtm();
    // Capture & persist any ?cp=<token> for Community Partner
    // attribution. Survives a tab close (localStorage) so it can be
    // forwarded to survey.haevn.co at survey-start time.
    captureCp(pathname);
    // Then: ping the backend visit log. Deduped client-side per
    // (visitor, path, utm) so a returning user gets counted once per
    // page within a session.
    pingVisit(pathname);
    // Finally: fire the GA4 page_view that was here before.
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.origin + path,
      page_title: document.title,
    });
  }, [pathname, search]);
  return null;
}

// Global click instrumentation for outbound survey links.
//
// The site has 15+ "Start Survey" / "Take the Survey" buttons across ~10
// pages. Some are wired to call trackSurveyStart() explicitly on click,
// many aren't. Rather than touching every CTA, this single document-
// level listener catches any click that resolves to a survey.haevn.co
// destination and fires trackSurveyStart() exactly once per click.
//
// trackSurveyStart() in turn:
//   • Fires the GA4 `survey_start` event
//   • Fires the Google Ads conversion (AW-18059485742/ebv2CK6ugZccEK7Et6ND)
//   • Pings /api/track/event for our UTM Report
//
// We use capture phase + closest() so we catch clicks on icons / inner
// spans inside the <a>, not just direct <a> hits. The "source" we pass
// is derived from data-testid when present (gives Ads / GA4 clean
// per-CTA segmentation in reports), falling back to the route path.
function SurveyClickTracker() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onClick = (e) => {
      try {
        const anchor = e.target && e.target.closest && e.target.closest("a");
        if (!anchor) return;
        const href = anchor.getAttribute("href") || "";
        if (!/(^|\/\/)survey\.haevn\.co/i.test(href)) return;
        // Forward Community Partner attribution to the survey host.
        // We can't share localStorage cross-origin, so we hop the token
        // on the URL. survey.haevn.co is expected to capture ?cp= on
        // landing and fire /api/community-partner/track/completion when
        // the survey is submitted.
        let outHref = appendCpToUrl(href);
        // Also append UTM context + visitor_id to the outbound URL so
        // survey.haevn.co can pass them straight back to /api/track/event
        // when it fires `survey_started` / `survey_completed`. Without
        // this hop, sessionStorage on haevn.co is invisible to the
        // survey domain. See /app/SURVEY_TRACKING.md.
        try {
          // eslint-disable-next-line global-require
          const { getStoredUtm, getVisitorId } = require("./lib/utm");
          const utm = getStoredUtm();
          const vid = getVisitorId();
          const url = new URL(outHref, window.location.origin);
          if (vid && !url.searchParams.has("haevn_visitor")) {
            url.searchParams.set("haevn_visitor", vid);
          }
          for (const [k, v] of Object.entries(utm || {})) {
            if (v && !url.searchParams.has(k)) {
              url.searchParams.set(k, v);
            }
          }
          outHref = url.toString();
        } catch {
          /* fall back to whatever we already had */
        }
        if (outHref !== href) {
          anchor.setAttribute("href", outHref);
        }
        // Skip if the page already wired up an inline tracker — avoids
        // double-firing. We mark fired clicks with a one-shot dataset
        // flag that's read inside the trampoline below.
        if (anchor.dataset.svtFired === "1") return;
        anchor.dataset.svtFired = "1";
        // Clear the flag on the next tick so future clicks still fire.
        setTimeout(() => {
          try { delete anchor.dataset.svtFired; } catch {}
        }, 0);
        const source =
          anchor.getAttribute("data-testid") ||
          (window.location.pathname || "unknown") + "_link";
        trackSurveyStart(source);
      } catch {
        /* swallow — never break navigation on a tracking failure */
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  return null;
}

// Minimal fallback — neutral so it never flashes a different brand color.
function RouteLoader() {
  // Neutral dark background matches the WhyNot slider and other dark
  // landing pages; on light pages this is barely visible under the
  // fallback's brief lifespan (~1 frame on eager routes). Keeping it
  // neutral prevents a white flash if a route ever regresses to lazy.
  return <div style={{ minHeight: "100vh", background: "#0A0E1A" }} aria-hidden="true" />;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <RouteTracker />
          <SurveyClickTracker />
          <Suspense fallback={<RouteLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/why-apps-feel-broken" element={<WhyPage />} />
              <Route path="/evolution" element={<EvolutionPage />} />
              <Route path="/what-to-expect" element={<WhatToExpectPage />} />
              <Route path="/match" element={<MatchExplainedPage />} />
              <Route path="/connect" element={<ConnectExplainedPage />} />
              <Route path="/verification" element={<VerificationPage />} />
              <Route path="/verification1" element={<VerificationSubpage />} />
              {/* Cities directory retired (user-approved 2026-02).
                  Membership is no longer gated by city activation; the
                  index + legacy variants redirect to home. Individual
                  city-specific SEO landing pages below remain. */}
              <Route path="/cities" element={<Navigate to="/" replace />} />
              <Route path="/cities-v1" element={<Navigate to="/" replace />} />
              <Route path="/cities1" element={<Navigate to="/" replace />} />
              <Route path="/waitlist/:slug" element={<CityWaitlistPage />} />
              <Route path="/join" element={<GeneralWaitlistPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/anatomy" element={<AnatomyPage />} />
              <Route path="/matching-model" element={<MatchingModelPage />} />
              <Route path="/how-80-works" element={<How80WorksPage />} />
              <Route path="/match-example" element={<MatchExamplePage />} />
              {/* BOQM event giveaway — QR code lands here */}
              <Route path="/boqm-giveaway" element={<BoqmGiveawayPage />} />
              <Route path="/boqm" element={<BoqmGiveawayPage />} />
              {/* Partner onboarding — internal Meetup Spot management portal.
                  Single splat route so React Router keeps the same element
                  mounted whether the URL is /partner-onboarding or any
                  nested path (e.g. /crm/{id}). Wrapped in QmoConfigProvider
                  so the CRM can hydrate its Category/City dropdowns from
                  the QMO app's config on load. */}
              <Route path="/partner-onboarding/*" element={<QmoConfigProvider><PartnerPortalPage /></QmoConfigProvider>} />
              {/* NEW CRM — canonical URL per approved Phase 2 scope.
                  Three approved screens (Workspace, Business Record,
                  Sales Overview). Reuses the existing portal auth token;
                  if missing, redirects to /partner-onboarding to sign in. */}
              <Route path="/crm/*" element={<CrmApp />} />
              <Route path="/field" element={<FieldIntakePage />} />
              <Route path="/f/:token" element={<RepFieldPage />} />
              {/* Meetup Spots public funnel — URL is printed on marketing
                  materials and must stay at exactly /meetup. */}
              <Route path="/meetup" element={<MeetupFunnelPage />} />
              <Route path="/meetup/manage" element={<MeetupManagePage />} />
              <Route path="/meetup/terms" element={<MeetupTermsPage />} />
              {/* Single splat route so the same CopPortalPage instance
                  stays mounted across /cop and every nested subpath —
                  otherwise React Router unmounts+remounts on transitions,
                  which briefly blanks the shell after actions like save. */}
              <Route path="/cop/*" element={<CopPortalPage />} />
              {/* Legacy public Stripe onboarding flow — kept for backup */}
              <Route path="/partner-onboarding-public" element={<PartnerOnboardingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/problem" element={<ProblemPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/careers/:slug" element={<JobDetailPage />} />
              <Route path="/apply" element={<ApplyPage />} />
              <Route path="/press" element={<PressPage />} />
              <Route path="/austin" element={<AustinLandingPage />} />
              <Route path="/cities/austin" element={<AustinSnapshotPage />} />
              <Route path="/cities/portland" element={<PortlandSnapshotPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              {/* HAEVN Live — guest-list landing page.
                  Public (no auth). Submits to /api/public/haevn-live/guest-list
                  which upserts into haevn_live_subscribers and syncs a
                  tagged CRM contact in portal_contacts. */}
              <Route path="/live" element={<HaevnLivePage />} />
              {/* /tyler — standalone matchmaking-service landing page.
                  No HAEVN nav/footer shell per approved brief. CTAs
                  deep-link to survey.haevn.co with locked UTMs. */}
              <Route path="/tyler" element={<TylerLandingPage />} />
              <Route path="/matchmaking" element={<MatchmakingLandingPage />} />
              <Route path="/no-swiping" element={<NoSwipingPage />} />
              <Route path="/real-matches" element={<RealMatchesPage />} />
              <Route path="/who-its-for" element={<WhoItsForPage />} />
              <Route path="/matching" element={<MatchingPage />} />
              <Route path="/match-monday" element={<MatchMondayPage />} />
              <Route path="/membership" element={<MembershipPage />} />
              <Route path="/founders" element={<FoundersPage />} />
              <Route path="/time" element={<TimePage />} />
              <Route path="/why-haevn" element={<WhyHaevnPage />} />
              <Route path="/no-photos" element={<NoPhotosPage />} />
              <Route path="/problem1" element={<ProblemSubpage />} />
              <Route path="/demo1" element={<DemoSubpage />} />
              <Route path="/perspective" element={<PerspectivePage />} />
              <Route path="/perspective/:slug" element={<PerspectiveDetailPage />} />
              <Route path="/podcast-host" element={<PodcastHostPage />} />
              <Route path="/swipe-bait" element={<SwipeBaitPage />} />
              <Route path="/start" element={<StartPage />} />
              <Route path="/portland" element={<PortlandPage />} />
              <Route path="/enm" element={<EnmPage />} />
              <Route path="/gay" element={<GayPage />} />
              <Route path="/poly" element={<PolyPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/why-not" element={<WhyNotPage />} />
              <Route path="/whynot" element={<WhyNotSliderPage />} />
              <Route path="/bq01" element={<WhyNotSliderPage surveyUrl="https://survey.haevn.co/bq01" pagePath="/bq01" trackingSource="bq01_slider" />} />
              <Route path="/fb01" element={<WhyNotSliderPage surveyUrl="https://survey.haevn.co/fb01" pagePath="/fb01" trackingSource="fb01_slider" />} />
              <Route path="/fb02" element={<WhyNotSliderPage slides={FB02_SLIDES} surveyUrl="https://survey.haevn.co/fb01" pagePath="/fb02" trackingSource="fb02_slider" />} />
              <Route path="/safetynet" element={<SafetynetSliderPage />} />
              {/* Community Partner Program — referral / affiliate network */}
              <Route path="/community-partner" element={<CommunityPartnerPage />} />
              <Route path="/affiliates/apply" element={<AffiliateApplyPage />} />
              <Route path="/affiliates/terms" element={<AffiliateTermsPage />} />
              <Route path="/affiliates/setup" element={<AffiliateSetupPage />} />
              <Route path="/affiliates/login" element={<AffiliateLoginPage />} />
              <Route path="/affiliates/dashboard" element={<AffiliateDashboardPage />} />
              <Route path="/affiliates/join" element={<AffiliateJoinPage />} />
              {/* /affiliate/* — the full authenticated Affiliate portal
                  (mirrors the /cop portal). Legacy /affiliates/dashboard
                  still renders the older card so we don't break links,
                  but the header link and setup CTAs both point to /affiliate. */}
              <Route path="/affiliate/*" element={<AffiliatePortalPage />} />
              <Route path="/haevn-university/affiliate" element={<HaevnUniversityAffiliatePage />} />
              <Route path="/r/:slug" element={<AffiliateReferralRedirectPage />} />
              {/* Phase 1: /affiliates (marketing landing) currently
                  redirects to the application until Phase 4 adds the
                  full public program page. */}
              <Route path="/affiliates" element={<AffiliateApplyPage />} />
              <Route path="/community-partner/terms" element={<CommunityPartnerTermsPage />} />
              <Route path="/community-partner/dashboard" element={<CommunityPartnerDashboardPage />} />
              {/* Dynamic warm-up landing page — /p/{slug} — digital business card for partners */}
              <Route path="/p/:slug" element={<PartnerWarmupPage />} />
              {/* Isolated design-system exploration — /design-lab. NOT linked
                  from any live surface. Marked noindex in the page head so
                  it never leaves the internal review loop. */}
              <Route path="/design-lab" element={<DesignLabPage />} />
              <Route path="/casting" element={<CastingPage />} />
              <Route path="/casting/:slug" element={<CastingProjectPage />} />
              <Route path="/10072026A" element={<Landing10072026A />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
